import { StandardEvents } from '@shopify/events';

/**
 * MAISON ARPENT — la mention de paiement fractionné.
 *
 * Deux responsabilités :
 *  1. suivre le changement de variante — l'échéance de la 200 × 300 n'est pas
 *     celle de la 120 × 170 — et masquer la mention hors des bornes du
 *     prestataire ;
 *  2. ouvrir et fermer la bulle d'explication.
 *
 * **Aucun calcul de prix ici.** Les échéances sont écrites en Liquid, formatées
 * par Shopify, dans la table `data-pf="donnees"` : le JS choisit une ligne, il
 * ne divise rien. Un arrondi fait en JS finirait par diverger de ce que le
 * tunnel de paiement prélève.
 *
 * Balisage : `blocks/arpent-paiement-fractionne.liquid`
 */
class PaiementFractionne extends HTMLElement {
  /** @type {{min:number, max:number, variantes:{id:number,prix:number,texte:string}[]}|null} */
  #donnees = null;
  /** @type {Element | null} */
  #contexte = null;
  #ecoutes = new AbortController();

  connectedCallback() {
    this.#lireDonnees();

    this.#contexte = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
    this.#contexte?.addEventListener(StandardEvents.productSelect, this.#surVariante, {
      signal: this.#ecoutes.signal,
    });

    this.#brancherBulle();
  }

  disconnectedCallback() {
    this.#ecoutes.abort();
    this.#contexte = null;
  }

  get #declencheur() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-pf="declencheur"]'));
  }

  get #bulle() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-pf="bulle"]'));
  }

  #lireDonnees() {
    const contenu = this.querySelector('[data-pf="donnees"]')?.textContent;
    if (!contenu) return;

    try {
      const brut = JSON.parse(contenu);
      if (Array.isArray(brut?.variantes)) this.#donnees = brut;
    } catch (erreur) {
      console.warn('[paiement-fractionne] table des échéances illisible', erreur);
    }
  }

  /** @param {any} evenement */
  #surVariante = (evenement) => {
    evenement.promise
      ?.then(({ detail }) => {
        const id = detail?.resource?.id;
        const donnees = this.#donnees;
        if (!id || !donnees) return;

        const ligne = donnees.variantes.find((v) => String(v.id) === String(id));
        if (!ligne) return;

        const montant = this.querySelector('[data-pf="montant"]');
        if (montant) montant.textContent = ligne.texte;

        /* Hors des bornes du prestataire, la mention se tait plutôt que de
           promettre un fractionnement qui ne sera pas proposé au paiement. */
        const trop_bas = donnees.min > 0 && ligne.prix < donnees.min;
        const trop_haut = donnees.max > 0 && ligne.prix > donnees.max;
        this.hidden = trop_bas || trop_haut;
        if (this.hidden) this.#fermer();
      })
      .catch((erreur) => {
        if (erreur?.name !== 'AbortError') console.warn('[paiement-fractionne]', erreur);
      });
  };

  #brancherBulle() {
    const declencheur = this.#declencheur;
    const bulle = this.#bulle;
    if (!declencheur || !bulle) return;

    const { signal } = this.#ecoutes;

    declencheur.addEventListener(
      'click',
      () => (declencheur.getAttribute('aria-expanded') === 'true' ? this.#fermer() : this.#ouvrir()),
      { signal }
    );

    /* Une bulle qui ne se referme ni au clic à côté ni à Échap est une bulle
       qu'on subit. Les deux écoutes vivent sur le document, le temps qu'elle
       est ouverte — l'`AbortController` les emporte au démontage. */
    document.addEventListener(
      'click',
      (evenement) => {
        if (declencheur.getAttribute('aria-expanded') !== 'true') return;
        if (evenement.target instanceof Node && this.contains(evenement.target)) return;
        this.#fermer();
      },
      { signal }
    );

    document.addEventListener(
      'keydown',
      (evenement) => {
        if (evenement.key !== 'Escape') return;
        if (declencheur.getAttribute('aria-expanded') !== 'true') return;
        this.#fermer();
        declencheur.focus();
      },
      { signal }
    );
  }

  #ouvrir() {
    this.#declencheur?.setAttribute('aria-expanded', 'true');
    if (this.#bulle) this.#bulle.hidden = false;
  }

  #fermer() {
    this.#declencheur?.setAttribute('aria-expanded', 'false');
    if (this.#bulle) this.#bulle.hidden = true;
  }
}

if (!customElements.get('paiement-fractionne')) {
  customElements.define('paiement-fractionne', PaiementFractionne);
}
